import { FeatureGeoJson } from "./../FeatureGeoJson";
import axios from "axios";
import Point from "ol/geom/Point";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import { Feature } from "ol";
import { Projection, fromLonLat, transformExtent } from "ol/proj";
import { bbox } from "ol/loadingstrategy.js";
import { ILayerFactory } from "./ILayerFactory";
import { Icon, RegularShape, Stroke, Style } from "ol/style";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { Map } from "ol";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { Layer } from "ol/layer";
import { FeatureType } from "../define/FeatureType";
import { AttributeType } from "../define/AttributeType";
import { IAttributeConvert } from "@/attribute/convert/IAttributeConvert";
import { LayerUtil } from "../util/LayerUtil"
/**
 * 対策要否型
 */
export type TaisakuYohi = "0" | "1" | "8";

/**
 * 措置状況型
 */
export type SochiJoukyou = "0" | "1" | "2" | "3" | "8";

/**
 * 判定区分型
 */
export type HanteiKubun = "1" | "2" | "3" | "4" | "8";

/**
 * 管理者区分型
 */
export type KanrishaKubun = "1" | "2" | "3" | "4" | "5" | "6";

export type RequestBody = {
  key: string;
  value: string;
};

/**
 * 点検データレイヤ出力 基底クラス
 */
export abstract class AbstractRoadStructuresLayerFactory
  implements ILayerFactory
{
  /**
   * Authorizationヘッダに与えるIDトークンを設定する
   * @param idToken Authorizationヘッダに与える値
   */
  static setIdToken(_idToken: string) {
    return;
  }

  /**
   * 属性キー
   */

  KEY_ATTRIBUTE = "attribute";

  /**
   * API取得用URL
   */

  abstract URL_API: string;

  /**
   * アイコン画像パス
   */

  abstract URL_LAYER_ICON: string;

  /**
   * API検索時の取得件数
   */

  VALUE_API_LIMIT = 1000;

  /**
   * アイコンのサイズ
   */

  abstract ICON_SIZE: number;

  /**
   * 最大ズームレベル
   */

  abstract LAYER_MAX_ZOOMLEVEL: number;

  /**
   * 最小ズームレベル
   */

  abstract LAYER_MIN_ZOOMLEVEL: number;

  /**
   * コンテキストメニュー名称
   */
  abstract CONTEXT_MENU_NAME: string;

  /**
   * 対象FeatureType
   */

  abstract FEATURE_TYPE: FeatureType;

  /**
   * 対象AttributeConvert
   */
  abstract ATTRIBUTE_CONVERT: (geoJSON: any) => IAttributeConvert;

  /**
   * 対象AttributeType
   */
  ATTRIBUTE_TYPE: AttributeType = AttributeType.Normal;

  /**
   * 対象データ出力レイヤ
   */
  private _viewLayer: VectorLayer<VectorSource>;

  /**
   * 対象データソース
   */
  private _vectorSource: VectorSource;

  /**
   * 絞り込み条件
   */
  private _taisakuYohiFilterList: TaisakuYohi[] = [];
  private _hanteiKubunFilterList: HanteiKubun[] = [];
  private _kanrishaKubunFilterList: KanrishaKubun[] = [];
  private _sochiJoukyouFilterList: SochiJoukyou[] = [];

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return false;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return false;
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    return this._viewLayer?.getVisible() ? 1 : 0;
  }

  /**
   * レイヤ定義を取得
   */
  getViewLayer(_map?: Map): VectorLayer<VectorSource>[] {
    if (this._viewLayer) {
      return [this._viewLayer];
    }
    this._vectorSource = new VectorSource({
      format: new GeoJSON(),
      loader: async (
        extent,
        _resolution,
        projection: Projection,
        success,
        failure
      ) => {
        //APIパラメータが経度緯度指定のため、Webメルカトルを経度緯度に変換
        const area = transformExtent(extent, projection.getCode(), "EPSG:4326");
        const url =
          this.URL_API +
          "?area=" +
          area.join(",") +
          "&limit=" +
          this.VALUE_API_LIMIT;
        await axios
          .get(url, {
            headers: {
              custom: "viewer",
            },
          })
          .then((res: any) => {
            const results = res.data.result;
            const features = new Array<Feature>();
            //データソースからデータを削除
            this._vectorSource.clear();
            //絞り込み条件外の場合、return
            results.forEach((result: any) => {
              if (
                (this._taisakuYohiFilterList.length &&
                  !this._taisakuYohiFilterList.includes(
                    result.tenken.kiroku.taisaku_yohi
                  )) ||
                !this._hanteiKubunFilterList.includes(
                  result.tenken.kiroku.hantei_kubun
                ) ||
                !this._kanrishaKubunFilterList.includes(
                  result.syogen.kanrisya.kubun
                ) ||
                !this._sochiJoukyouFilterList.includes(
                  result.tenken.syuzen.sochi_joukyou
                )
              )
                return;
              const detailInfo = result.syogen;
              const ronLat = [detailInfo.ichi.keido, detailInfo.ichi.ido];
              // Webメルカトルに変換
              const geometry = new Point(fromLonLat(ronLat));
              // Featureを作成
              const feature = new Feature({
                geometry,
                name: detailInfo.shisetsu.meisyou,
              });
              // idをセット（FeatureTypeのvalue-施設ID-点検年度）
              const id =
                this.FEATURE_TYPE.toString() +
                "-" +
                result.shisetsu_id +
                "-" +
                result.tenken.nendo;
              feature.setId(id);
              // アイコンのスタイルを作成・セット
              const iconStyle = new Style({
                image: new Icon({
                  src: this.URL_LAYER_ICON,
                  scale: this.ICON_SIZE,
                }),
              });
              feature.setStyle(iconStyle);
              // 属性キーを登録
              feature.set(this.KEY_ATTRIBUTE, result);
              features.push(feature);
            });
            this._vectorSource.addFeatures(features);
            success(features);
          })
          .catch((err) => {
            console.error(err);
            failure();
          });
      },
      strategy: bbox,
    });
    this._viewLayer = new VectorLayer({
      source: this._vectorSource,
      maxZoom: this.LAYER_MAX_ZOOMLEVEL,
      minZoom: this.LAYER_MIN_ZOOMLEVEL,
    });
    return [this._viewLayer];
  }

  /**
   * 地物情報を取得
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > this.LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= this.LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    const pixel = [x, y];
    const vectorFeatures = map.getFeaturesAtPixel(pixel, {
      layerFilter: (layer: Layer) => {
        if (this._viewLayer == layer) {
          return true;
        }
      },
    });
    if (vectorFeatures.length !== 0) {
      const selectedFeatures = vectorFeatures.map((featureLike) => {
        // FeatureLike型をFeatureにキャスト
        const feature = new Feature(featureLike.getProperties());
        // キャストでIDがセットされないため、別途IDをセット
        feature.setId(featureLike.getId());

        const geometry = feature.getGeometry().clone();
        const selectFeature = new Feature({
          geometry,
        });
        // スタイルのセット
        const selectStyle = [
          new Style({
            image: new Icon({
              src: this.URL_LAYER_ICON,
              scale: this.ICON_SIZE,
            }),
            stroke: new Stroke({ width: 2, color: "hotpink" }),
          }),
          new Style({
            image: new RegularShape({
              stroke: new Stroke({ width: 4, color: "hotpink" }),
              points: 4,
              radius: 21,
              angle: Math.PI / 4,
            }),
          }),
        ];
        selectFeature.setStyle(selectStyle);

        // GeoJSONを生成
        const geoJson = new GeoJSON().writeFeature(feature);
        // feature内Idを取得
        const fId = feature.getId().toString();
        const featureGeoJson = new FeatureGeoJson(geoJson, fId);
        // 作成したデータをもとにSelectedFeatureInfoを作成してreturn
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          this.CONTEXT_MENU_NAME,
          featureGeoJson,
          this.ATTRIBUTE_TYPE,
          this.ATTRIBUTE_CONVERT(geoJson)
        );
        return selectedFeatureInfo;
      });
      return [...selectedFeatures];
    }
    return [];
  }

  /**
   * 右クリック処理
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getRightClickInfo(
    _map: Map,
    _latitude: number,
    _longitude: number,
    _x: number,
    _y: number,
    _width: number,
    _height: number,
    _bBox: number[]
  ): Promise<MapContextMenuItem[]> {
    return null;
  }

  /**
   * 指定範囲の地物を取得
   * @param layerNames レイヤ名リスト
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    map: Map,
    _bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > this.LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= this.LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    const features = this._viewLayer.getSource().getFeatures();
    if (features.length !== 0) {
      const rangeFeatureInfoList: SelectedFeatureInfo[] = features.map(
        (feature) => {
          // featureからgeometryを取得し、地理情報のみのFeatureを作成
          const geometry = feature.getGeometry().clone();
          const selectFeature = new Feature({
            geometry,
          });

          // GeoJSONを生成
          const geoJson = new GeoJSON().writeFeature(feature);
          // feature内Idを取得
          const fId = feature.getId().toString();
          const featureGeoJson = new FeatureGeoJson(geoJson, fId);

          // 作成したデータをもとにSelectedFeatureInfoを作成してreturn
          const selectedFeatureInfo = new SelectedFeatureInfo(
            selectFeature,
            feature.get("name"),
            featureGeoJson,
            this.ATTRIBUTE_TYPE,
            this.ATTRIBUTE_CONVERT(geoJson)
          );
          return selectedFeatureInfo;
        }
      );
      return [...rangeFeatureInfoList];
    }
    return null;
  }

  /**
   * 絞り込み条件変更メソッド
   * @param isVisible 表示有無
   * @param sochiJoukyou 措置状況
   * @param hanteiKubun 判定区分
   * @param kanrishaKubunList 管理者区分リスト
   * @param sochiJoukyouList 措置状況リスト
   */
  setVisible(
    isVisible: boolean,
    taisakuYohiList: TaisakuYohi[] = [],
    hanteiKubunList: HanteiKubun[] = [],
    kanrishaKubunList: KanrishaKubun[] = [],
    sochiJoukyouList: SochiJoukyou[] = []
  ): void {
    if (isVisible) {
      // isVisible: trueの場合、引数の条件を追加
      this._taisakuYohiFilterList = [...taisakuYohiList];
      this._hanteiKubunFilterList = [...hanteiKubunList];
      this._kanrishaKubunFilterList = [...kanrishaKubunList];
      this._sochiJoukyouFilterList = [...sochiJoukyouList];
      // viewLayerのsourceを更新
      this._vectorSource.refresh();
      this._viewLayer.setVisible(true);
    } else {
      // レイヤそのものを非表示に設定
      this._viewLayer.setVisible(false);
    }
  }

  /**
   * 表示レイヤが存在するか判定
   */
   isVisibleLayer(_map:Map) {
    return LayerUtil.isVisibleLayer(_map, this._viewLayer)
  }
}
